import React from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { getCompanies } from 'apis/UserAPI';

import { CompaniesContext } from './CompaniesContext';
import CompaniesData from './CompaniesData';
import CompaniesHeader from './CompaniesHeader';
import { COMPANIES_PAGE_SIZE } from './constants';
import { CompanyListItemType } from './table/types';
import useDataList from '../../utils/hooks/useDataList';
import { getUserSwitchRedirectPath, queueSupportModeRedirectPath, useUserSwitch } from '../layout/hooks';
import { useRootContext } from '../layout/RootContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const Companies = () => {
  const navigate = useNavigate();
  const { showMessage } = useRootContext();
  const { handleUserSwitch } = useUserSwitch();

  const { items, totalItemsCount, filters, setItems, setPage, setFilters } = useDataList({
    initialState: {
      filters: {
        page: 1,
        pageSize: COMPANIES_PAGE_SIZE,
        search: '',
        status: [],
        shops_per_year: [],
        shop_months: [],
      },
    },
  });

  const { isLoading, isFetching, refetch } = useQuery({
    queryKey: [filters],
    queryFn: () =>
      getCompanies({
        search: filters.search,
        page: filters.page,
        page_size: filters.pageSize,
        status: filters.status,
        shops_per_year: filters.shops_per_year,
        shop_months: filters.shop_months,
      }),
    onSuccess: (data: any) => {
      setItems(data);
    },
    refetchOnWindowFocus: false,
  });

  const resetFilters = () => {
    setFilters({
      page: 1,
      search: '',
      status: [],
      shops_per_year: [],
      shop_months: [],
    });
  };

  const onEnterSupportMode = (item: CompanyListItemType) => {
    if (!item.primary_user?.id) {
      showMessage('error', 'Company has no associated primary user.');
      return;
    }
    handleUserSwitch(item.primary_user?.id ?? -1, (userInfo) => {
      const redirectPath = getUserSwitchRedirectPath(userInfo);
      queueSupportModeRedirectPath(redirectPath);
      navigate(redirectPath);
    });
  };

  return (
    <CompaniesContext.Provider
      value={{
        loading: isLoading || isFetching,
        items: items ?? [],
        totalItemsCount,
        filters,
        setItems,
        setPage,
        setFilters,
        resetFilters,
        refetch,
        onEnterSupportMode,
      }}
    >
      <Container>
        <CompaniesHeader />
        <CompaniesData />
      </Container>
    </CompaniesContext.Provider>
  );
};

export default Companies;
